import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { api } from "../../lib/api";
import { LayoutDashboard, Package, ShoppingBag, Users, FileText, Megaphone, Ban, ArrowLeft, Wallet, Inbox } from "lucide-react";

const SECTIONS = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/payments", label: "Payments", icon: Wallet },
  { to: "/admin/messages", label: "Messages", icon: Inbox },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/coas", label: "COAs", icon: FileText },
  { to: "/admin/announcements", label: "Announcements", icon: Megaphone },
  { to: "/admin/restrictions", label: "Restrictions", icon: Ban },
];

const UnreadBadge = ({ count, testid }) => (count > 0 ? (
  <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#C96F53] text-white text-[11px] font-semibold inline-flex items-center justify-center" aria-label={`${count} unread`} data-testid={testid}>
    {count > 99 ? "99+" : count}
  </span>
) : null);

// Polls the unread contact-message count; refreshes on navigation and when Messages changes
const useUnread = (enabled, getToken) => {
  const { pathname } = useLocation();
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const load = async () => {
      try {
        const r = await api.get("/admin/messages/unread-count", { headers: { Authorization: `Bearer ${await getToken()}` } });
        setCount(r.data.count);
      } catch { /* keep last value */ }
    };
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("kocm:messages-changed", load);
    return () => { clearInterval(t); window.removeEventListener("kocm:messages-changed", load); };
  }, [enabled, getToken, pathname]);
  return count;
};

export default function Admin() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [role, setRole] = useState(null);
  const [checking, setChecking] = useState(true);
  const unread = useUnread(role === "admin", getToken);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await api.get("/me", { headers: { Authorization: `Bearer ${token}` } });
        setRole(r.data.role);
      } catch {
        setRole("none");
      }
      setChecking(false);
    })();
  }, [getToken]);

  if (checking) return <div className="p-20 text-center text-[#5E4D5A]">Checking access…</div>;
  if (role !== "admin") {
    return (
      <div className="max-w-xl mx-auto p-20 text-center" data-testid="admin-forbidden">
        <h1 className="font-serif text-3xl mb-4">Not permitted</h1>
        <p className="text-[#5E4D5A] mb-6">This area is restricted to KOCM administrators.</p>
        <button onClick={() => navigate("/")} className="bg-[#59394F] text-white rounded-full px-6 py-3 text-sm">Return home</button>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row min-h-screen">
      <nav className="lg:hidden border-b border-[#A8B59A]/20 bg-white overflow-x-auto" data-testid="admin-mobile-nav">
        <div className="flex gap-1 p-2 w-max">
          <Link to="/" className="flex items-center gap-1 px-3 py-2 text-xs text-[#5E4D5A]" data-testid="admin-mobile-back"><ArrowLeft size={12} /> Site</Link>
          {unread > 0 && (
            <Link to="/admin/messages" className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs whitespace-nowrap bg-[#C96F53]/15 text-[#8E4A36] font-semibold" data-testid="admin-mobile-unread-pill">
              <Inbox size={14} /> {unread > 99 ? "99+" : unread} new
            </Link>
          )}
          {SECTIONS.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-full text-xs whitespace-nowrap transition-colors ${isActive ? "bg-[#C99898]/15 text-[#5A433B]" : "text-[#5E4D5A]"}`} data-testid={`admin-mnav-${label.toLowerCase()}`}>
              <Icon size={14} /> {label}
              {to === "/admin/messages" && <UnreadBadge count={unread} testid="admin-mnav-unread" />}
            </NavLink>
          ))}
        </div>
      </nav>
      <aside className="w-64 bg-white border-r border-[#A8B59A]/20 flex-shrink-0 hidden lg:block">
        <div className="p-6 border-b border-[#A8B59A]/20">
          <Link to="/" className="text-xs text-[#5E4D5A] inline-flex items-center gap-1 hover:text-[#59394F]"><ArrowLeft size={12} /> Back to site</Link>
          <p className="font-serif text-xl mt-3">Admin</p>
        </div>
        <nav className="p-3">
          {SECTIONS.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm mb-1 transition-colors ${isActive ? "bg-[#C99898]/15 text-[#5A433B]" : "text-[#5E4D5A] hover:bg-[#F4EBDD]"}`} data-testid={`admin-nav-${label.toLowerCase()}`}>
              <Icon size={16} /> {label}
              {to === "/admin/messages" && <UnreadBadge count={unread} testid="admin-nav-unread" />}
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="flex-1 overflow-x-hidden">
        <Outlet />
      </div>
    </div>
  );
}
